/**
 * DangerZonePanel — irreversible account deletion.
 *
 * Deletion is guarded by a typed-email confirmation: the destructive button is
 * disabled until the user types their own email exactly. On confirm it calls
 * the (audit-logged) ``DELETE /me`` endpoint via {@link useDeleteAccount} and
 * signs the user out.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState, type ReactElement } from "react";
import { useDeleteAccount } from "./hooks/useDeleteAccount";
import { useUser } from "@/auth/AuthProvider";

export function DangerZonePanel(): ReactElement {
  const user = useUser();
  const del = useDeleteAccount();
  const [typed, setTyped] = useState("");

  const email = user?.email ?? "";
  // Require an exact, case-insensitive match of the signed-in email.
  const confirmed = email.length > 0 && typed.trim().toLowerCase() === email.toLowerCase();

  return (
    <Box
      component="section"
      aria-label="Danger zone"
      sx={{ border: 1, borderColor: "error.main", borderRadius: 1, p: 2 }}
    >
      <Typography variant="h6" component="h2" color="error" gutterBottom>
        Danger zone
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Deleting your account is permanent and cannot be undone. All saved searches, notifications,
        and webhooks are removed. Type your email <strong>{email}</strong> to confirm.
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 480, mt: 1 }}>
        <TextField
          label="Confirm your email"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoComplete="off"
          fullWidth
        />
        <Box>
          <Button
            color="error"
            variant="contained"
            disabled={!confirmed || del.isPending}
            onClick={() => del.mutate()}
          >
            Delete my account
          </Button>
        </Box>
        {del.isError ? (
          <Alert severity="error">Could not delete your account. Please try again.</Alert>
        ) : null}
      </Stack>
    </Box>
  );
}
